// Инспектор оформления виджета: чтение и запись контролов, сброс к умолчаниям.

import type { VisualizationType, WidgetAppearance, WidgetDefinition, WidgetTimeSettings } from '../types';

import { DEFAULT_APPEARANCE, DEFAULT_DONUT_INNER_RADIUS, DEFAULT_TIME, normalizeAppearance } from './model';
import { clearFormError, errorMessage, markDirty, showFormError } from './status';
import { controls } from './controls';
import { isMapVisualization, parseManualBreaks, updateClassificationControls, updateConditionControls, updateVisualizationSpecificControls } from './widget-form';
import { replaceWidgetInGrid } from './widgets';
import { selectedWidget, updateInspectorState } from './workspace';
import { state } from './state';

export function defaultWidgetLayout(visualization: VisualizationType): WidgetDefinition['layout'] {
  const y = state.definition.widgets.reduce((max, widget) => Math.max(max, widget.layout.y + widget.layout.h), 0);
  if (visualization === 'kpi') return { x: 0, y, w: 4, h: 3 };
  if (visualization === 'table') return { x: 0, y, w: 8, h: 5 };
  if (visualization === 'heatmap') return { x: 0, y, w: 8, h: 5 };
  if (visualization === 'population_pyramid') return { x: 0, y, w: 6, h: 5 };
  if (visualization === 'network' || visualization === 'flow') return { x: 0, y, w: 6, h: 5 };
  if (visualization === 'small_multiples_bar' || visualization === 'district_mini_maps') return { x: 0, y, w: 8, h: 5 };
  if (isMapVisualization(visualization)) return { x: 0, y, w: 6, h: 5 };
  return { x: 0, y, w: 6, h: 4 };
}

export function optionalNumber(input: HTMLInputElement): number | null {
  if (!input.value.trim()) return null;
  const value = Number(input.value);
  if (!Number.isFinite(value)) throw new Error(`Поле «${input.labels?.[0]?.textContent?.trim() ?? 'число'}» заполнено неверно.`);
  return value;
}

export function readConditionValue(): number | null {
  const value = controls.conditionOperator.value === 'none' || controls.conditionValue.value === ''
    ? null
    : Number(controls.conditionValue.value);
  if (value != null && !Number.isFinite(value)) throw new Error('Порог условия должен быть числом.');
  return value;
}

export function readAppearanceControls(): WidgetAppearance {
  const conditionValue = readConditionValue();
  const mapFixedMin = optionalNumber(controls.mapFixedMin);
  const mapFixedMax = optionalNumber(controls.mapFixedMax);
  if (controls.mapScaleMode.value === 'fixed' && (mapFixedMin == null || mapFixedMax == null || mapFixedMax <= mapFixedMin)) {
    throw new Error('Для фиксированной шкалы укажите максимум, превышающий минимум.');
  }
  const heatmapFixedMin = optionalNumber(controls.heatmapFixedMin);
  const heatmapFixedMax = optionalNumber(controls.heatmapFixedMax);
  if (controls.heatmapScaleMode.value === 'fixed' && (heatmapFixedMin == null || heatmapFixedMax == null || heatmapFixedMax <= heatmapFixedMin)) {
    throw new Error('Для фиксированной шкалы heatmap укажите максимум, превышающий минимум.');
  }
  const pieVariant = controls.pieVariant.value as WidgetAppearance['pieVariant'];
  const pieInnerRadius = pieVariant === 'donut'
    ? Number(controls.pieInnerRadius.value) || DEFAULT_DONUT_INNER_RADIUS
    : 0;
  return normalizeAppearance({
    numberFormat: controls.numberFormat.value as WidgetAppearance['numberFormat'],
    showUnit: controls.showUnit.checked,
    labelMode: controls.labelMode.value as WidgetAppearance['labelMode'],
    showTooltip: controls.tooltip.checked,
    showGrid: controls.grid.checked,
    animation: controls.animation.value as WidgetAppearance['animation'],
    palette: controls.palette.value as WidgetAppearance['palette'],
    classification: controls.classification.value as WidgetAppearance['classification'],
    classCount: Math.max(2, Math.min(9, Number(controls.classCount.value) || 5)),
    manualBreaks: parseManualBreaks(controls.manualBreaks.value),
    reversePalette: controls.reverse.checked,
    showLegend: controls.legend.checked,
    legendPosition: controls.legendPosition.value as WidgetAppearance['legendPosition'],
    showLabels: controls.labels.checked,
    nullColor: controls.nullColor.value,
    decimals: Math.max(0, Math.min(6, Number(controls.decimals.value) || 0)),
    orientation: controls.orientation.value as WidgetAppearance['orientation'],
    sortOrder: controls.sortOrder.value as WidgetAppearance['sortOrder'],
    topN: Math.max(0, Math.min(100, Number(controls.topN.value) || 0)),
    axisScale: controls.axisScale.value as WidgetAppearance['axisScale'],
    includeZero: controls.includeZero.value as WidgetAppearance['includeZero'],
    xAxisMin: optionalNumber(controls.xAxisMin),
    xAxisMax: optionalNumber(controls.xAxisMax),
    axisMin: optionalNumber(controls.axisMin),
    axisMax: optionalNumber(controls.axisMax),
    smoothLines: controls.smooth.checked,
    lineWidth: Number(controls.lineWidth.value) || DEFAULT_APPEARANCE.lineWidth,
    lineStyle: controls.lineStyle.value as WidgetAppearance['lineStyle'],
    lineCurve: controls.lineCurve.value as WidgetAppearance['lineCurve'],
    connectNulls: controls.connectNulls.checked,
    areaOpacity: Number(controls.areaOpacity.value) || DEFAULT_APPEARANCE.areaOpacity,
    areaFillMode: controls.areaFillMode.value as WidgetAppearance['areaFillMode'],
    seriesStackMode: controls.barLayout.value === 'stacked' ? 'stacked' : controls.barLayout.value === 'percent' ? 'percent' : 'none',
    showPoints: controls.showPoints.value as WidgetAppearance['showPoints'],
    dataZoom: controls.dataZoom.value as WidgetAppearance['dataZoom'],
    usePatterns: controls.patterns.checked,
    barLayout: controls.barLayout.value as WidgetAppearance['barLayout'],
    barWidth: optionalNumber(controls.barWidth),
    barGap: Number(controls.barGap.value) || DEFAULT_APPEARANCE.barGap,
    showTotalLabels: controls.totalLabels.checked,
    barLabelPosition: controls.barLabelPosition.value as WidgetAppearance['barLabelPosition'],
    otherThreshold: Number(controls.otherThreshold.value) || 0,
    pieVariant,
    pieInnerRadius,
    pieOuterRadius: Number(controls.pieOuterRadius.value) || DEFAULT_APPEARANCE.pieOuterRadius,
    pieLabelMode: controls.pieLabelMode.value as WidgetAppearance['pieLabelMode'],
    pieMinSlicePercent: Number(controls.otherThreshold.value) || 0,
    pieStartAngle: Number(controls.pieStartAngle.value) || DEFAULT_APPEARANCE.pieStartAngle,
    pieRoseMode: controls.pieRoseMode.value as WidgetAppearance['pieRoseMode'],
    heatmapTranspose: controls.heatmapTranspose.checked,
    heatmapScaleMode: controls.heatmapScaleMode.value as WidgetAppearance['heatmapScaleMode'],
    heatmapFixedMin,
    heatmapFixedMax,
    heatmapMissingColor: controls.heatmapMissingColor.value,
    heatmapCellGap: Number(controls.heatmapCellGap.value) || 0,
    heatmapCellBorder: controls.heatmapCellBorder.checked,
    heatmapAggregation: DEFAULT_APPEARANCE.heatmapAggregation,
    pyramidSexDimension: controls.pyramidSexDimension.value.trim() || DEFAULT_APPEARANCE.pyramidSexDimension,
    pyramidAgeDimension: controls.pyramidAgeDimension.value.trim() || DEFAULT_APPEARANCE.pyramidAgeDimension,
    pyramidMaleValue: controls.pyramidMaleValue.value.trim() || DEFAULT_APPEARANCE.pyramidMaleValue,
    pyramidFemaleValue: controls.pyramidFemaleValue.value.trim() || DEFAULT_APPEARANCE.pyramidFemaleValue,
    pyramidMode: controls.pyramidMode.value as WidgetAppearance['pyramidMode'],
    pyramidMaleColor: controls.pyramidMaleColor.value,
    pyramidFemaleColor: controls.pyramidFemaleColor.value,
    pyramidAgeOrder: DEFAULT_APPEARANCE.pyramidAgeOrder,
    kpiIconMode: controls.kpiIconMode.value as WidgetAppearance['kpiIconMode'],
    kpiIcon: controls.kpiIcon.value.trim() || DEFAULT_APPEARANCE.kpiIcon,
    kpiAggregation: controls.kpiAggregation.value as WidgetAppearance['kpiAggregation'],
    kpiComparison: controls.kpiComparison.value as WidgetAppearance['kpiComparison'],
    kpiTarget: optionalNumber(controls.kpiTarget),
    kpiSparkline: controls.kpiSparkline.checked,
    kpiSparklineStyle: controls.kpiSparklineStyle.value as WidgetAppearance['kpiSparklineStyle'],
    tableColumns: controls.tableColumns.value as WidgetAppearance['tableColumns'],
    tableDensity: controls.tableDensity.value as WidgetAppearance['tableDensity'],
    tablePageSize: Math.max(5, Math.min(100, Number(controls.tablePageSize.value) || DEFAULT_APPEARANCE.tablePageSize)),
    tableShowQuality: controls.tableQuality.checked,
    tableCellVisual: controls.tableCellVisual.value as WidgetAppearance['tableCellVisual'],
    mapScaleMode: controls.mapScaleMode.value as WidgetAppearance['mapScaleMode'],
    mapFixedMin,
    mapFixedMax,
    mapLegendMode: controls.mapLegendMode.value as WidgetAppearance['mapLegendMode'],
    mapBasemap: controls.mapBasemap.value as WidgetAppearance['mapBasemap'],
    mapFillOpacity: Number(controls.mapFillOpacity.value) || DEFAULT_APPEARANCE.mapFillOpacity,
    mapOutlineColor: controls.mapOutlineColor.value,
    mapOutlineWidth: Number(controls.mapOutlineWidth.value) || DEFAULT_APPEARANCE.mapOutlineWidth,
    mapBasemapOpacity: Number(controls.mapBasemapOpacity.value) || DEFAULT_APPEARANCE.mapBasemapOpacity,
    mapSymbolMinRadius: Number(controls.mapSymbolMinRadius.value) || DEFAULT_APPEARANCE.mapSymbolMinRadius,
    mapSymbolMaxRadius: Number(controls.mapSymbolMaxRadius.value) || DEFAULT_APPEARANCE.mapSymbolMaxRadius,
    mapSymbolScale: controls.mapSymbolScale.value as WidgetAppearance['mapSymbolScale'],
    mapSymbolStrokeColor: controls.mapSymbolStrokeColor.value,
    mapSymbolStrokeWidth: Number(controls.mapSymbolStrokeWidth.value) || DEFAULT_APPEARANCE.mapSymbolStrokeWidth,
    mapSymbolOpacity: Number(controls.mapSymbolOpacity.value) || DEFAULT_APPEARANCE.mapSymbolOpacity,
    scatterBubbleMode: DEFAULT_APPEARANCE.scatterBubbleMode,
    networkMode: DEFAULT_APPEARANCE.networkMode,
    flowMode: DEFAULT_APPEARANCE.flowMode,
    flowMapMode: DEFAULT_APPEARANCE.flowMapMode,
    miniMapGroup: DEFAULT_APPEARANCE.miniMapGroup,
    condition: {
      operator: controls.conditionOperator.value as WidgetAppearance['condition']['operator'],
      value: conditionValue,
      color: controls.conditionColor.value,
    },
  });
}

export function syncKpiIconControls(): void {
  const custom = controls.kpiIconMode.value === 'custom';
  controls.kpiIconPickerField.hidden = !custom;
  controls.kpiIconCustomField.hidden = !custom;
  const selectedIcon = controls.kpiIcon.value.trim() || DEFAULT_APPEARANCE.kpiIcon;
  controls.kpiIconPicker.querySelectorAll<HTMLButtonElement>('[data-kpi-icon]').forEach((button) => {
    const active = button.dataset.kpiIcon === selectedIcon;
    button.classList.toggle('active', active);
    button.setAttribute('aria-selected', String(active));
  });
}

export function writeAppearanceControls(appearance: Partial<WidgetAppearance>, title = '', time: Partial<WidgetTimeSettings> = DEFAULT_TIME): void {
  const normalized = normalizeAppearance(appearance);
  controls.title.value = title;
  controls.numberFormat.value = normalized.numberFormat;
  controls.showUnit.checked = normalized.showUnit;
  controls.labelMode.value = normalized.labelMode;
  controls.tooltip.checked = normalized.showTooltip;
  controls.grid.checked = normalized.showGrid;
  controls.animation.value = normalized.animation;
  controls.palette.value = normalized.palette;
  controls.decimals.value = String(normalized.decimals);
  controls.orientation.value = normalized.orientation;
  controls.sortOrder.value = normalized.sortOrder;
  controls.topN.value = String(normalized.topN);
  controls.axisScale.value = normalized.axisScale;
  controls.includeZero.value = normalized.includeZero;
  controls.xAxisMin.value = normalized.xAxisMin == null ? '' : String(normalized.xAxisMin);
  controls.xAxisMax.value = normalized.xAxisMax == null ? '' : String(normalized.xAxisMax);
  controls.axisMin.value = normalized.axisMin == null ? '' : String(normalized.axisMin);
  controls.axisMax.value = normalized.axisMax == null ? '' : String(normalized.axisMax);
  controls.dataZoom.value = normalized.dataZoom;
  controls.showPoints.value = normalized.showPoints;
  controls.legendPosition.value = normalized.legendPosition;
  controls.smooth.checked = normalized.smoothLines || normalized.lineCurve === 'smooth';
  controls.patterns.checked = normalized.usePatterns;
  controls.legend.checked = normalized.showLegend;
  controls.labels.checked = normalized.showLabels;
  controls.reverse.checked = normalized.reversePalette;
  controls.classification.value = normalized.classification;
  controls.classCount.value = String(normalized.classCount);
  controls.manualBreaks.value = normalized.manualBreaks.join('; ');
  controls.nullColor.value = normalized.nullColor;
  controls.lineWidth.value = String(normalized.lineWidth);
  controls.lineStyle.value = normalized.lineStyle;
  controls.lineCurve.value = normalized.lineCurve;
  controls.connectNulls.checked = normalized.connectNulls;
  controls.areaOpacity.value = String(normalized.areaOpacity);
  controls.areaFillMode.value = normalized.areaFillMode;
  controls.barLayout.value = normalized.barLayout;
  controls.barWidth.value = normalized.barWidth == null ? '' : String(normalized.barWidth);
  controls.barGap.value = String(normalized.barGap);
  controls.totalLabels.checked = normalized.showTotalLabels;
  controls.barLabelPosition.value = normalized.barLabelPosition;
  controls.otherThreshold.value = String(normalized.otherThreshold);
  controls.pieVariant.value = normalized.pieVariant;
  controls.pieInnerRadius.value = String(normalized.pieVariant === 'donut' ? normalized.pieInnerRadius || DEFAULT_DONUT_INNER_RADIUS : 0);
  controls.pieInnerRadius.disabled = normalized.pieVariant !== 'donut';
  controls.pieOuterRadius.value = String(normalized.pieOuterRadius);
  controls.pieLabelMode.value = normalized.pieLabelMode;
  controls.pieStartAngle.value = String(normalized.pieStartAngle);
  controls.pieRoseMode.value = normalized.pieRoseMode;
  controls.heatmapTranspose.checked = normalized.heatmapTranspose;
  controls.heatmapScaleMode.value = normalized.heatmapScaleMode;
  controls.heatmapFixedMin.value = normalized.heatmapFixedMin == null ? '' : String(normalized.heatmapFixedMin);
  controls.heatmapFixedMax.value = normalized.heatmapFixedMax == null ? '' : String(normalized.heatmapFixedMax);
  controls.heatmapMissingColor.value = normalized.heatmapMissingColor;
  controls.heatmapCellGap.value = String(normalized.heatmapCellGap);
  controls.heatmapCellBorder.checked = normalized.heatmapCellBorder;
  controls.pyramidSexDimension.value = normalized.pyramidSexDimension;
  controls.pyramidAgeDimension.value = normalized.pyramidAgeDimension;
  controls.pyramidMaleValue.value = normalized.pyramidMaleValue;
  controls.pyramidFemaleValue.value = normalized.pyramidFemaleValue;
  controls.pyramidMode.value = normalized.pyramidMode;
  controls.pyramidMaleColor.value = normalized.pyramidMaleColor;
  controls.pyramidFemaleColor.value = normalized.pyramidFemaleColor;
  controls.kpiIconMode.value = normalized.kpiIconMode;
  controls.kpiIcon.value = normalized.kpiIcon;
  syncKpiIconControls();
  controls.kpiAggregation.value = normalized.kpiAggregation;
  controls.kpiComparison.value = normalized.kpiComparison;
  controls.kpiTarget.value = normalized.kpiTarget == null ? '' : String(normalized.kpiTarget);
  controls.kpiSparkline.checked = normalized.kpiSparkline;
  controls.kpiSparklineStyle.value = normalized.kpiSparklineStyle;
  controls.tableColumns.value = normalized.tableColumns;
  controls.tableDensity.value = normalized.tableDensity;
  controls.tablePageSize.value = String(normalized.tablePageSize);
  controls.tableCellVisual.value = normalized.tableCellVisual;
  controls.tableQuality.checked = normalized.tableShowQuality;
  controls.mapScaleMode.value = normalized.mapScaleMode;
  controls.mapLegendMode.value = normalized.mapLegendMode;
  controls.mapBasemap.value = normalized.mapBasemap;
  controls.mapFixedMin.value = normalized.mapFixedMin == null ? '' : String(normalized.mapFixedMin);
  controls.mapFixedMax.value = normalized.mapFixedMax == null ? '' : String(normalized.mapFixedMax);
  controls.mapFillOpacity.value = String(normalized.mapFillOpacity);
  controls.mapOutlineColor.value = normalized.mapOutlineColor;
  controls.mapOutlineWidth.value = String(normalized.mapOutlineWidth);
  controls.mapBasemapOpacity.value = String(normalized.mapBasemapOpacity);
  controls.mapSymbolMinRadius.value = String(normalized.mapSymbolMinRadius);
  controls.mapSymbolMaxRadius.value = String(normalized.mapSymbolMaxRadius);
  controls.mapSymbolScale.value = normalized.mapSymbolScale;
  controls.mapSymbolStrokeColor.value = normalized.mapSymbolStrokeColor;
  controls.mapSymbolStrokeWidth.value = String(normalized.mapSymbolStrokeWidth);
  controls.mapSymbolOpacity.value = String(normalized.mapSymbolOpacity);
  controls.timeControl.checked = time.showControl ?? DEFAULT_TIME.showControl;
  controls.conditionOperator.value = normalized.condition.operator;
  controls.conditionValue.value = normalized.condition.value == null ? '' : String(normalized.condition.value);
  controls.conditionColor.value = normalized.condition.color;
}

export function readAppearanceFromControls(widget: WidgetDefinition): Pick<WidgetDefinition, 'title' | 'titleEn' | 'appearance' | 'time'> {
  const nextAppearance = readAppearanceControls();
  return {
    title: controls.title.value.trim() || widget.title,
    titleEn: controls.titleEn.value.trim() || widget.titleEn,
    appearance: normalizeAppearance({
      ...widget.appearance,
      ...nextAppearance,
      scatterBubbleMode: widget.appearance.scatterBubbleMode ?? nextAppearance.scatterBubbleMode,
      networkMode: widget.appearance.networkMode ?? nextAppearance.networkMode,
      flowMode: widget.appearance.flowMode ?? nextAppearance.flowMode,
      flowMapMode: widget.appearance.flowMapMode ?? nextAppearance.flowMapMode,
      miniMapGroup: widget.appearance.miniMapGroup ?? nextAppearance.miniMapGroup,
    }),
    time: {
      activePeriod: widget.time.activePeriod,
      showControl: controls.timeControl.checked,
    },
  };
}

export function applyAppearanceToSelected(force = false): void {
  if (state.suppressInspectorEvents && !force) return;
  const widget = selectedWidget();
  if (!widget) return;
  try {
    const next = readAppearanceFromControls(widget);
    widget.title = next.title;
    if (next.titleEn) widget.titleEn = next.titleEn;
    widget.appearance = next.appearance;
    widget.time = next.time;
    replaceWidgetInGrid(widget, false);
    updateVisualizationSpecificControls();
    updateInspectorState();
    markDirty();
    clearFormError();
  } catch (error) {
    showFormError(errorMessage(error));
  }
}

export function resetAppearanceForSelected(): void {
  const widget = selectedWidget();
  if (!widget) return;
  state.suppressInspectorEvents = true;
  controls.title.value = widget.title;
  controls.titleEn.value = widget.titleEn ?? '';
  controls.palette.value = DEFAULT_APPEARANCE.palette;
  controls.decimals.value = String(DEFAULT_APPEARANCE.decimals);
  controls.orientation.value = DEFAULT_APPEARANCE.orientation;
  controls.sortOrder.value = DEFAULT_APPEARANCE.sortOrder;
  controls.topN.value = String(DEFAULT_APPEARANCE.topN);
  controls.axisScale.value = DEFAULT_APPEARANCE.axisScale;
  controls.includeZero.value = DEFAULT_APPEARANCE.includeZero;
  controls.xAxisMin.value = '';
  controls.xAxisMax.value = '';
  controls.dataZoom.value = DEFAULT_APPEARANCE.dataZoom;
  controls.showPoints.value = DEFAULT_APPEARANCE.showPoints;
  controls.legendPosition.value = DEFAULT_APPEARANCE.legendPosition;
  controls.smooth.checked = DEFAULT_APPEARANCE.smoothLines;
  controls.patterns.checked = DEFAULT_APPEARANCE.usePatterns;
  controls.legend.checked = DEFAULT_APPEARANCE.showLegend;
  controls.labels.checked = DEFAULT_APPEARANCE.showLabels;
  controls.reverse.checked = DEFAULT_APPEARANCE.reversePalette;
  controls.classification.value = DEFAULT_APPEARANCE.classification;
  controls.classCount.value = String(DEFAULT_APPEARANCE.classCount);
  controls.manualBreaks.value = '';
  controls.nullColor.value = DEFAULT_APPEARANCE.nullColor;
  controls.mapScaleMode.value = DEFAULT_APPEARANCE.mapScaleMode;
  controls.mapBasemap.value = DEFAULT_APPEARANCE.mapBasemap;
  controls.mapFixedMin.value = '';
  controls.mapFixedMax.value = '';
  controls.timeControl.checked = DEFAULT_TIME.showControl;
  controls.conditionOperator.value = 'none';
  controls.conditionValue.value = '';
  controls.conditionColor.value = DEFAULT_APPEARANCE.condition.color;
  writeAppearanceControls(DEFAULT_APPEARANCE, widget.title, DEFAULT_TIME);
  state.suppressInspectorEvents = false;
  updateClassificationControls();
  updateConditionControls();
  updateVisualizationSpecificControls();
  applyAppearanceToSelected();
}
